import { Base } from '@/components/Base';
import { Consent } from '@/components/Consent';
import { JwVideoTeaser } from '@/components/JwVideo/Teaser';
import { ListGrid } from '@/components/ListGrid';
import { PageMeta } from '@/components/PageMeta';
import { SchemaMarkup } from '@/components/SchemaMarkup';
import { Tracking } from '@/components/Tracking';
import { VideoBaseContent } from '@/components/VideoBaseContent';
import { componentTheme } from '@/styles/JwVideoTeaser';
import { Content } from '@/types/content';
import getCurrentBrand from '@/utils/getCurrentBrand';
import { mergeOptions } from '@/utils/merge';
import { VIDEO_PAGES_CONFIG } from 'base/configs/video';
import { VideoAllShowsPageData, getAllShowsPageData } from 'lib/data/video';
import { omitBy } from 'lodash-es';
import { GetServerSideProps, NextPage } from 'next';
import { isUndefined } from 'typesafe-utils';

const brand = getCurrentBrand();

const pageData: Content = {
  meta: {},
  type: 'videoWebPage',
  children: [],
  data: omitBy(
    {
      brand: brand.name,
      titleSuffix: ' | Video',
      seoTitle: VIDEO_PAGES_CONFIG.allShowPage?.seoTitle,
      domain: brand.host,
      canonical: `${process.env.OCELOT_DOMAIN}video/show`,
      seoDescription: VIDEO_PAGES_CONFIG.allShowPage?.seoDescription,
      ogImageUrl: `${process.env.OCELOT_DOMAIN}/image/${brand.slug}-logo.svg`,
    },
    isUndefined,
  ),
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const data = (await getAllShowsPageData()) ?? {};

  // ### Cache Headers ###
  const cacheControl = process.env.VERCEL
    ? `private, no-cache, no-store, max-age=0, must-revalidate`
    : `public, max-age=3600`;

  context.res.setHeader('cache-control', cacheControl); // This is set for Cloudflare
  // ### Cache Headers ###

  return { props: data };
};

const ShowsPage: NextPage<VideoAllShowsPageData> = (props) => {
  const { shows, tagsList } = props;

  return (
    <Base
      content={
        <>
          <Consent />
          <PageMeta pageData={pageData} />
          <SchemaMarkup pageData={pageData} />
          <Tracking />

          <VideoBaseContent
            label="Populärt:"
            tagsList={tagsList}
            breadcrumbs={[{ label: 'Video start', url: '/video' }, { label: 'Shows' }]}
            headline={VIDEO_PAGES_CONFIG.allShowPage?.headline}
            description={VIDEO_PAGES_CONFIG.allShowPage?.description}
          >
            <ListGrid
              content={shows.playlist?.map((media) => (
                <JwVideoTeaser
                  key={media.mediaId}
                  mediaId={media.mediaId}
                  thumbnail={media.image}
                  isStillImage
                  title={media.title}
                  teaserLink={media.url}
                  options={mergeOptions({
                    theme: componentTheme('video_pages'),
                    $link: {
                      target: '_self',
                    },
                    variant: 'vertical',
                    $playIcon: { className: '!block' },
                    $media: {
                      $image: {
                        src: media.image,
                        size: 'vertical',
                      },
                    },
                    $content: {
                      colors: 'default',
                      $tag: {
                        theme: componentTheme('tag'),
                      },
                    },
                  })}
                />
              ))}
            />
          </VideoBaseContent>
        </>
      }
    />
  );
};

export default ShowsPage;
